import { useCallback, useEffect, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { ClientsService } from "../services/clients";
import { selectUserRoles } from "../../SharedModule/redux/reducers/auth.reducer";
import { addToast } from "../../SharedModule/redux/reducers/ui.reducer";
import {
  ROUTE_CLIENTS,
  ROUTE_FORBIDDEN_PAGE,
} from "../../SharedModule/utils/constants";
import { useNavigate } from "react-router";
import {
  IsLoadedPriceExceptions,
  IsLoadingPriceExceptions,
  loadPriceExceptionTab,
  SelectClientPriceExceptions,
  setIsLoadingPriceExceptions,
} from "../redux/reducers/clientPriceExceptions.reducer";
import { TimesheetsService } from "../../TimesModule/services/timesheets";
import {
  IsClientsLoaded,
  SelectActualClientForHeaderInfo,
  SelectClientsIds,
  setActualClientForHeaderInfo,
  setIsActualClientForHeaderInfoLoading,
} from "../redux/reducers/clientsList.reducer";
import { Client } from "../types/clients-module-types";

import { useClientsList } from "./useClientsList";

export const useClientPriceExceptions = (clientId: number) => {
  useClientsList();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { userRolesList, rolesLoaded } = useSelector(selectUserRoles);
  const isLoadingPriceExceptions = useSelector(IsLoadingPriceExceptions);
  const isLoadedPriceExceptions = useSelector(IsLoadedPriceExceptions);
  // Ref that mirrors isLoadedPriceExceptions so the callback can read the
  // latest value without listing it as a useCallback dep.  Same pattern as
  // hasInitiallyLoadedRef in useTimes — prevents the callback from getting a
  // new reference (and triggering useEffect again) when the flag flips true
  // after the first successful fetch.
  const isLoadedPriceExceptionsRef = useRef(isLoadedPriceExceptions);
  isLoadedPriceExceptionsRef.current = isLoadedPriceExceptions;
  const clientPriceExceptions = useSelector(SelectClientPriceExceptions);

  // Tracks fetch blocks in flight. isLoadedPriceExceptionsRef above only
  // records "already loaded" — it doesn't stop two overlapping invocations
  // (e.g. from a dependency change re-running the effect while the first
  // fetch is still pending) from both reading it as false and firing twice.
  const inFlightRef = useRef<Record<string, boolean>>({});
  const headerDoneRef = useRef<number | undefined>(undefined);

  // To check is header info is already loaded
  const ClientForHeaderInfo: Client = useSelector(
    SelectActualClientForHeaderInfo
  );
  // to check if clientId is in list of clients that user could see
  const isClientsLoaded = useSelector(IsClientsLoaded);
  const clientsIds = useSelector(SelectClientsIds);

  const loadClientPriceExceptions = useCallback(
    async (clientId) => {
      if (rolesLoaded && isClientsLoaded) {
        // This check should go in all clients custom hooks
        if (!clientsIds.includes(clientId)) {
          dispatch(
            addToast({
              mode: "error",
              message: "You're not allowed to see this Client Page",
            })
          );
          navigate(ROUTE_FORBIDDEN_PAGE);
          return true;
        }
        // Load info header when user put a new URL
        if (
          !inFlightRef.current.header &&
          headerDoneRef.current !== clientId &&
          (!ClientForHeaderInfo ||
            (ClientForHeaderInfo && ClientForHeaderInfo.id !== clientId))
        ) {
          inFlightRef.current.header = true;
          dispatch(setIsActualClientForHeaderInfoLoading(true));
          let headerInfo = await ClientsService.getClientModuleHeaderInfo(
            clientId
          )
            .then((response) => {
              if (!response.success) {
                dispatch(
                  addToast({
                    mode: "error",
                    message: response.message,
                  })
                );
                dispatch(setIsActualClientForHeaderInfoLoading(false));
                navigate(ROUTE_CLIENTS);
                return true;
              } else {
                return response.data;
              }
            })
            .catch(() => {
              dispatch(
                addToast({
                  mode: "error",
                  message:
                    "Something wrong happened while getting the client header information.",
                })
              );
              dispatch(setIsActualClientForHeaderInfoLoading(false));
              navigate(ROUTE_CLIENTS);
              return true;
            });
          inFlightRef.current.header = false;
          headerDoneRef.current = clientId;
          dispatch(setActualClientForHeaderInfo(headerInfo));
        }
        // page info — only fetch if not already loaded for this session.
        // clientPriceExceptions is an array so .id was always undefined,
        // making the old third condition always true and causing an infinite loop.
        if (!inFlightRef.current.priceExceptions && !isLoadedPriceExceptionsRef.current) {
          inFlightRef.current.priceExceptions = true;
          dispatch(setIsLoadingPriceExceptions(true));
          const priceExceptionsInfo =
            await TimesheetsService.getPriceExceptionsByClient(clientId)
              .then((data) => {
                if (!data.success) {
                  dispatch(
                    addToast({
                      mode: "error",
                      message: "Client not found",
                    })
                  );
                  dispatch(setIsLoadingPriceExceptions(false));
                  navigate(ROUTE_CLIENTS);
                  return true;
                } else {
                  return data;
                }
              })
              .catch(() => {
                dispatch(
                  addToast({
                    mode: "error",
                    message: "Client not found",
                  })
                );
                dispatch(setIsLoadingPriceExceptions(false));
                navigate(ROUTE_CLIENTS);
                return true;
              });
          inFlightRef.current.priceExceptions = false;
          dispatch(loadPriceExceptionTab(priceExceptionsInfo));
        }
      }
    },
    [dispatch, rolesLoaded, isClientsLoaded]
  );

  useEffect(() => {
    loadClientPriceExceptions(clientId);
  }, [loadClientPriceExceptions]);

  return {
    isLoadingPriceExceptions,
    clientPriceExceptions,
    userRolesList,
    rolesLoaded,
  };
};
